import { t } from 'i18next'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'
import { easeInOutTransition, focusedInput } from '~/components/CommonStyles'
import { dimensions, light, spacing } from '~/constants/theme'

import Text from './Text'

const RootWrapper = styled.div`
  width: 100%;
  display: block;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${light.transparent};
  flex-direction: ${(props) =>
    props.direct === 'vertical' ? 'column' : 'row'};
  align-items: ${(props) =>
    props.direct === 'vertical' ? 'flex-start' : 'center'};
  justify-content: ${(props) =>
    props.direct === 'vertical' ? 'center' : 'space-between'};
`

const InputPrefixWrapper = styled.div`
  background-color: ${light.gray300};
  padding: 0px ${spacing.spacingMD};
  border-radius: ${dimensions.borderRadiusSM} 0px 0px
    ${dimensions.borderRadiusSM};
  display: flex;
  justify-content: center;
  color: ${light.dark700};
  align-items: center;
`

const LabelWrapper = styled.div`
  width: 100%;
  padding-bottom: ${spacing.spacingSM};
  font-weight: bold;
  display: block;
`

const InputWrapper = styled.div`
  width: ${(props) => (props.label ? '100%' : '100%')};
  border: 1px solid ${light.dark700};
  border-radius: ${dimensions.borderRadiusSM};
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  ${easeInOutTransition};

  :focus-within {
    ${focusedInput}
  }
`

const InputBox = styled.input`
  width: 100%;
  padding: ${spacing.spacingMD};
  display: block;
  border-radius: ${dimensions.borderRadiusSM};
  border: none;
  outline: none;
  color: ${light.teal5};
  font-family: 'Inter', Helvetica, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 17px;
  background-color: ${light.gray100};
  ${easeInOutTransition};

  &::placeholder,
  &::-webkit-input-placeholder {
    color: ${light.dark700};
  }
`

const Input = React.forwardRef(
  (
    {
      label,
      noBorder,
      placeholder,
      direction,
      type,
      value,
      error,
      onBlur,
      onChange,
      onKeyDown,
      autoFocus,
      prefix,
    },
    ref,
  ) => {
    const handleChange = (e) => {
      onChange(e.target.value)
    }

    return (
      <RootWrapper>
        {label && (
          <LabelWrapper>
            <Text size="normal" text={label} color={light.dark700} />
          </LabelWrapper>
        )}
        <InputWrapper
          direct={direction}
          error={error}
          label={label}
          noBorder={noBorder}
        >
          {prefix && (
            <InputPrefixWrapper>
              <Text size="small" text={prefix} color={light.dark700} />
            </InputPrefixWrapper>
          )}
          <InputBox
            placeholder={placeholder}
            ref={ref}
            type={type}
            value={value}
            onBlur={onBlur}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
          />
        </InputWrapper>
      </RootWrapper>
    )
  },
)

Input.propTypes = {
  error: PropTypes.bool,
  direction: PropTypes.oneOf(['vertical', 'horizontal']),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['email', 'file', 'date', 'password', 'tel', 'text']),
  value: PropTypes.string,
  noBorder: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  autoFocus: PropTypes.bool,
  prefix: PropTypes.string,
}

Input.defaultProps = {
  error: false,
  direction: 'horizontal',
  label: null,
  placeholder: t('global.input.defaultPlaceholder'),
  type: 'text',
  value: '',
  noBorder: false,
  autoFocus: false,
  onBlur: () => {},
  onChange: () => {},
  onKeyDown: () => {},
}

export default Input
